// The About animation as one timeline: time (s) -> every element's state.
// Time comes from the scroll position (src/about/About.tsx).
//
// From the Jitter file (1680x1000 frame, cards 312x502, radius 12):
//   0.62-1.42  the full-screen photo shrinks into a card (width and height together;
//              in Jitter the width first)
//   1.42-3.02  five cards fan out vertically, each turning back on its own
//              horizontal axis, the top one ahead (at 3.02: 110, 95, 85, 70, 50deg);
//              layered grey on top ... the last card at the bottom
//   3.02       layered the other way round (the last card on top): from here on they're
//              seen from the back
//   3.02-4.62  they carry on turning to 180deg while closing into a stack again
//   4.62-5.42  the card on top grows into the modal (in Jitter it turns sideways
//              as well)
// Jitter's 3D ("Card Flip"): every card has its own camera, straight in front of
// its centre, mix(8, 0.6, perspective) half card heights away (perspective 0.45
// there: 4.67), so each card is a symmetric trapezoid wherever it is on screen.
// The card's back shows the same colour.
// Jitter's keyframes at Jitter's times, but not its easings: each card's turn
// runs through its keyframes on one smooth curve (no stops or jolts between
// phases), its move out and back in on another.

export const CARD = {
    w: 312,
    h: 502,
    r: 12,
    end: 5.75,
}
/** camera distance, in half card heights (Jitter's Card Flip at perspective 0.45) */
const CAMERA = 8 + (0.6 - 8) * 0.45

// top to bottom: how far each card moves when the deck is open (frame px)
const SPREAD = [-352, -223, -115, 61, 325]
/** fanned out sideways (landscape screens) the spread is a little wider */
const SIDE = 1.15
// and its turn (deg) at Jitter's keyframes
const TURNS = [
    [0, 80, 110, 180],
    [0, 60, 95, 180],
    [0, 40, 85, 180],
    [0, 20, 70, 180],
    [0, 0, 50, 180],
]
// (the turn starts at 1.1, not Jitter's 1.42: while the photo is still shrinking,
// so the one flows into the other instead of starting from a stop)
const KEYS = [1.1, 2.22, 3.02, 4.62]

const T = {
    shrink: [0.5, 1.6],
    radius: [0.5, 1.3],
    open: [1.2, 3.02],
    close: [3.02, 4.62],
    // the modal's growth starts while the deck is still closing (its card on top
    // already widening during the last of its turn), so the two are one movement
    grow: [3.6, 5.35],
    modal: [4.62, 5.42],
    content: [5.05, 5.65],
}

const clamp = (x: number) => Math.max(0, Math.min(1, x))
/** smooth 0..1 between a and b (zero speed and acceleration at both ends) */
const ss = (a: number, b: number, x: number) => {
    const k = clamp((x - a) / (b - a))
    return k * k * k * (k * (k * 6 - 15) + 10)
}
const lerp = (a: number, b: number, k: number) => a + (b - a) * k

/** A smooth curve through keyframes (monotone cubic: never overshoots, never
 *  runs backwards, at rest at the first and last). */
function through(xs: number[], ys: number[]) {
    const n = xs.length
    const d = xs.slice(1).map((x, i) => (ys[i + 1] - ys[i]) / (x - xs[i]))
    const m = xs.map((_, i) => (i === 0 || i === n - 1 ? 0 : d[i - 1] * d[i] <= 0 ? 0 : (2 * d[i - 1] * d[i]) / (d[i - 1] + d[i])))
    return (x: number) => {
        if (x <= xs[0]) return ys[0]
        if (x >= xs[n - 1]) return ys[n - 1]
        let i = 0
        while (x > xs[i + 1]) i++
        const h = xs[i + 1] - xs[i],
            k = (x - xs[i]) / h
        const k2 = k * k,
            k3 = k2 * k
        return (2 * k3 - 3 * k2 + 1) * ys[i] + (k3 - 2 * k2 + k) * h * m[i] + (-2 * k3 + 3 * k2) * ys[i + 1] + (k3 - k2) * h * m[i + 1]
    }
}
const TURN = TURNS.map((ys) => through(KEYS, ys))
// Portrait (phones): the photo card already turns while it shrinks (from 0.7),
// the shrink landing softly under the turn, the deck opening sooner: one
// gesture instead of shrink, then turn.
const PORTRAIT = { keys: [0.7, 2.22, 3.02, 4.62], shrink: [0.45, 1.75], open: 1.0 }
const TURN_P = TURNS.map((ys) => through(PORTRAIT.keys, ys))

// The symmetric fan (/aabout2): six cards, evenly spread around a gap in the
// middle, each half the mirror image of the other (a card's mirror image turns
// the other way), opening outwards: the inner edges towards the camera, the
// outer ones away (from the outside in 35, 50, 65 | -65, -50, -35deg at the
// widest, all the way there symmetric).
const SPREAD_SYM = [-425, -255, -85, 85, 255, 425]
const TURNS_SYM = [35, 50, 65, -65, -50, -35].map((a) => [0, a * 0.55, a, 180])
// (the turn comes to rest at that pose at 3.02, the camera flying on from there:
// the middle card stays edge on, a line the camera passes)
const TURN_SYM = TURNS_SYM.map((ys) => through(KEYS.slice(0, 3), ys.slice(0, 3)))
const TURN_SYM_P = TURNS_SYM.map((ys) => through(PORTRAIT.keys.slice(0, 3), ys.slice(0, 3)))

/** A card's top and bottom on screen (relative to its centre), turned rx deg
 *  over its horizontal axis and seen by its own camera persp px away (as CSS
 *  rotateX after perspective()). */
function outline(h: number, rx: number, persp: number): [number, number] {
    const a = (rx * Math.PI) / 180
    const edge = (y: number) => {
        const yy = y * Math.cos(a),
            z = y * Math.sin(a)
        return (yy * persp) / (persp - z)
    }
    const e1 = edge(-h / 2),
        e2 = edge(h / 2)
    return [Math.min(e1, e2), Math.max(e1, e2)]
}

/** cubic-bezier easing (as CSS) */
function bezier(x1: number, y1: number, x2: number, y2: number) {
    const f = (a: number, b: number, k: number) => 3 * (1 - k) * (1 - k) * k * a + 3 * (1 - k) * k * k * b + k * k * k
    return (x: number) => {
        let lo = 0,
            hi = 1
        for (let i = 0; i < 24; i++) {
            const m = (lo + hi) / 2
            if (f(x1, x2, m) < x) lo = m
            else hi = m
        }
        return f(y1, y2, (lo + hi) / 2)
    }
}
/** the deck opens quickly (gaps between the cards early on) and settles at its widest */
const OPEN = bezier(0.35, 0, 0.15, 1)
/** the photo shrinks with a gentle start and a long, soft landing, the deck
 *  already turning and opening under its last part */
const SHRINK = bezier(0.42, 0, 0.18, 1)
/** the modal's growth: a soft start under the deck's last turn, a long landing */
const GROW = bezier(0.5, 0, 0.2, 1)

export type CardState = { w: number; h: number; r: number; x: number; y: number; cx: number; cy: number; z: number; blur: number; rx: number; scale: number; opacity: number; mark: number; layer: number; persp: number; visible: boolean }
export type Frame = {
    scale: number
    /** the cards drawn in real depth order (the fly-in) instead of layered */
    depth?: boolean
    /** the axis the deck fans out along: y (down, turning over the horizontal
     *  axis, as in Jitter) on portrait screens, x (sideways, turning over the
     *  vertical axis) on landscape ones */
    axis: "x" | "y"
    cards: CardState[]
    modal: { visible: boolean; w: number; h: number; r: number; ry: number; persp: number; content: number; opacity: number; scale: number }
    /** the intro text over the full-screen photo: gone as the photo starts to shrink */
    intro: number
}

export function timeline(t: number, vw: number, vh: number, sym = false): Frame {
    const phone = vw < 700
    const side = vw > vh
    // the frame's scale (Jitter's frame is 1000 high), the card narrower than the
    // screen; fanned out sideways, the card's height and the spread's width count
    const s = side
        ? Math.min((vh * 0.64) / CARD.h, vw / (2 * SIDE * 352 + CARD.w * 1.25))
        : Math.min(1.1, vh / (phone ? 1150 : 1060), (vw * (phone ? 0.55 : 0.5)) / CARD.w)
    const W = CARD.w * s,
        H = CARD.h * s,
        R = CARD.r * s

    // out to the spread and back in, at rest at the widest
    const open0 = side ? T.open[0] : PORTRAIT.open
    const [shrink0, shrink1] = side ? T.shrink : PORTRAIT.shrink
    const turn = sym ? (side ? TURN_SYM : TURN_SYM_P) : side ? TURN : TURN_P
    const spread = sym ? SPREAD_SYM : side ? EVEN.x : EVEN.y
    const o = t < T.open[1] ? OPEN(clamp((t - open0) / (T.open[1] - open0))) : 1 - ss(T.close[0], T.close[1], t)
    const done = t >= T.modal[0]
    // seen from the front, the top card is on top; from the back (Jitter: from 3.02), the bottom one
    const back = t >= T.close[0]

    const cards: CardState[] = spread.map((dy, i) => ({
        w: W,
        h: H,
        r: R,
        x: side ? dy * s * SIDE * o : 0,
        y: side ? 0 : dy * s * o,
        rx: turn[i](t),
        opacity: 1,
        // the NTO mark on its back (fades from the card that grows into the modal)
        mark: 1,
        // its camera: in front of its centre (as Jitter's Card Flip), not moved
        cx: 0,
        cy: 0,
        z: 0,
        blur: 0,
        // The cards under the one on top are a little smaller while the deck is
        // (nearly) stacked: turned less far than the top card, their corners would
        // show beside its edge going away. Full size as they come out from under it.
        scale:
            1 -
            0.1 *
                (t < T.close[0]
                    ? i > 0
                        ? 1 - ss(open0, open0 + 0.9, t)
                        : 0
                    : i < spread.length - 1
                      ? ss(T.close[1] - 0.8, T.close[1], t)
                      : 0),
        layer: back ? i + 1 : spread.length - i,
        persp: CAMERA * (H / 2),
        // (the cards under the photo wait until the deck opens: stacked exactly
        // under it, their edges would show around its rounded corners)
        visible: !done && (i === 0 || t > open0),
    }))
    // While the deck closes its cards lean towards the top (the upper ones are
    // turned over further): the whole deck is moved so its outline on screen is
    // centred. Nothing moves at the widest (3.02) or once stacked (4.62).
    const kc = clamp((t - T.close[0]) / (T.close[1] - T.close[0]))
    const centre = Math.sin(Math.PI * kc) ** 2
    if (centre > 0) {
        let top = Infinity,
            bottom = -Infinity
        for (const c of cards) {
            const at = side ? c.x : c.y
            const [a, b] = outline(side ? c.w : c.h, c.rx, c.persp)
            top = Math.min(top, at + a)
            bottom = Math.max(bottom, at + b)
        }
        const shift = -((top + bottom) / 2) * centre
        cards.forEach((c) => (side ? (c.x += shift) : (c.y += shift)))
    }
    // the photo: full screen, shrinking into the top card
    const kw = SHRINK(clamp((t - shrink0) / (shrink1 - shrink0))),
        kh = kw
    cards[0].w = lerp(vw, W, kw)
    cards[0].h = lerp(vh, H, kh)
    cards[0].r = R * ss(T.radius[0], T.radius[1], t)

    // The dark grey card on top grows into the modal (in Jitter it turns sideways
    // as well): it starts growing during the last of the deck's turn, and the
    // modal takes over from it once the deck is closed (same place, size and colour).
    const k = GROW(clamp((t - T.grow[0]) / (T.grow[1] - T.grow[0])))
    const MW = phone ? vw - 32 : Math.min(1000 * s, vw - 48)
    const MH = phone ? Math.min(vh - 180, 560) : H
    const top = cards[spread.length - 1]
    if (k > 0) {
        top.w = lerp(W, MW, k)
        top.h = lerp(H, MH, k)
        top.mark = 1 - clamp(k * 2.5)
    }
    cards.forEach((c) => ((c.cx = c.x), (c.cy = c.y)))
    return {
        scale: s,
        axis: side ? "x" : "y",
        intro: 1 - ss(shrink0, shrink0 + 0.3, t),
        cards,
        modal: {
            visible: done,
            w: lerp(W, MW, k),
            h: lerp(H, MH, k),
            r: R,
            ry: 0,
            persp: CAMERA * (H / 2),
            content: ss(T.content[0], T.content[1], t),
            opacity: 1,
            scale: 1,
        },
    }
}

/** The versions of the animation: "deck" closes the deck and grows its top card
 *  into the modal (/aabout); "fly" flies the camera into the open deck instead,
 *  then fades and scales in the modal (/aabout2). */
export type Variant = "deck" | "fly"

const FLY = {
    // the camera starts moving while the deck is still opening (easing in, so the
    // one flows into the other), and is through it by 4.5
    dolly: [2.55, 4.5],
    // the cameras of the cards become one, at the gap, by this time
    join: 3.15,
    // how far the camera moves in: this share of its distance to the cards
    reach: 0.93,
    // how far the cards turn towards edge on (share of their angle to it)
    open: 0.55,
    // blur (px) and dimming as the camera passes through
    blur: 6,
    dim: 0.35,
    fade: [3.75, 4.35],
    modal: [3.95, 4.75],
    grow: [3.95, 5.0],
    content: [4.3, 5.0],
}

/** Where the cards go when the deck is open: Jitter's first and last place
 *  (-352 and 325), the ones in between placed so the gaps between the cards
 *  on screen are all the same at the widest (3.02), each card taking the room
 *  its turn gives it there (seen by its own camera). Sideways the spread is
 *  SIDE wider, and the card's width counts instead of its height. */
const EVEN = (() => {
    const at = TURNS.map((k) => k[2])
    const place = (size: number, wide: number) => {
        const ext = at.map((rx) => outline(size, rx, CAMERA * (CARD.h / 2)))
        const first = SPREAD[0] * wide,
            last = SPREAD[SPREAD.length - 1] * wide
        // p[i+1] = p[i] + ext[i].bottom - ext[i+1].top + gap, with p[0], p[n-1] fixed
        let sum = 0
        for (let i = 0; i < at.length - 1; i++) sum += ext[i][1] - ext[i + 1][0]
        const gap = (last - first - sum) / (at.length - 1)
        const p = [first]
        for (let i = 0; i < at.length - 1; i++) p.push(p[i] + ext[i][1] - ext[i + 1][0] + gap)
        return p.map((v) => v / wide)
    }
    return { y: place(CARD.h, 1), x: place(CARD.w, SIDE) }
})()

/** the edge-on angle nearest a card's turn (+90 or -90deg) */
const edgeOn = (a: number) => (a < 0 ? -90 : 90)

/** a moment of the animation, as seen in one of its versions */
export function frame(t: number, vw: number, vh: number, variant: Variant = "deck"): Frame {
    if (variant === "deck") return timeline(t, vw, vh)
    if (t <= FLY.dolly[0]) return timeline(t, vw, vh, true)
    // the open deck as at 3.02 (no closing), at rest in its symmetric pose
    const tb = Math.min(t, T.close[0])
    const f = timeline(tb, vw, vh, true)
    // The camera flies in, into the gap in the middle of the deck. Up to now each
    // card had its own camera; they come together into one (at the gap) as the
    // flight starts, which then moves forward into the scene: the cards near the
    // middle come close and pass by in strong perspective, the others move out.
    // Meanwhile the cards open up (turning towards edge on: a corridor to fly
    // down), blur and dim a little, and fade out before the modal comes in.
    const side = f.axis === "x"
    const gap = 0
    const k = clamp((t - FLY.dolly[0]) / (FLY.dolly[1] - FLY.dolly[0]))
    const join = ss(FLY.dolly[0], FLY.join, t)
    const advance = k * k * (2.2 - 1.2 * k)
    const opening = ss(FLY.dolly[0] + 0.2, FLY.dolly[1] - 0.3, t)
    const fade = 1 - ss(FLY.fade[0], FLY.fade[1], t)
    f.depth = true
    f.cards.forEach((c) => {
        const own = side ? c.x : c.y
        const cam = lerp(own, gap, join)
        if (side) c.cx = cam
        else c.cy = cam
        c.z = c.persp * FLY.reach * advance
        c.rx = edgeOn(c.rx) + (c.rx - edgeOn(c.rx)) * (1 - FLY.open * opening)
        c.blur = FLY.blur * ss(FLY.dolly[0] + 0.3, FLY.fade[1], t)
        c.opacity = fade * (1 - FLY.dim * ss(FLY.dolly[0] + 0.3, FLY.fade[0], t))
        c.visible = c.visible && c.opacity > 0
    })
    // then the modal: fading in, growing to its size from a little smaller
    const m = timeline(T.modal[1] + 1, vw, vh).modal
    const mo = ss(FLY.modal[0], FLY.modal[1], t)
    f.modal = { ...m, visible: mo > 0, opacity: mo, scale: 0.86 + 0.14 * GROW(clamp((t - FLY.grow[0]) / (FLY.grow[1] - FLY.grow[0]))), content: ss(FLY.content[0], FLY.content[1], t) }
    return f
}

// ---------------------------------------------------------------------------
// /aabout2 as a real 3D scene (src/about/gl.ts draws it).
//
// One camera, on the centre line, looking straight ahead at the deck. Units are
// screen pixels: the deck's plane is D in front of the camera and seen at its
// own size there (so the full-screen photo is the screen). The deck is laid
// out symmetrically around that centre line, so the composition is centred by
// construction. The camera then moves forward (the only thing that moves the
// view: perspective changes as it really would), through the gap between the
// two middle cards and past the deck, while the cards keep opening towards
// edge on (walls to pass between) and a black layer comes over them.

export type Card3D = {
    /** centre on the deck's plane (px, y down as on screen) */
    x: number
    y: number
    w: number
    h: number
    r: number
    /** turn (deg) over the deck's axis: the vertical one fanned out sideways, the horizontal one otherwise */
    angle: number
    scale: number
    /** drawing order before depth takes over (higher on top) */
    layer: number
    /** the black layer over it, 0..1 */
    dark: number
    visible: boolean
}
export type Scene = {
    axis: "x" | "y"
    scale: number
    /** the camera's distance to the deck's plane at rest, and where it is now (0 = on the plane) */
    distance: number
    camera: number
    /** drawn by depth (true) or in layer order (false: while the deck is still stacked) */
    depth: boolean
    cards: Card3D[]
    modal: Frame["modal"]
    intro: number
}

const FLY3 = {
    // the camera: at rest until 2.45, then moving forward (setting off gently,
    // getting faster), through the deck's plane at about 3.9, a little past it by 4.5
    move: [2.45, 4.5],
    travel: 1.35,
    // the cards keep opening towards edge on as the camera comes in (share of
    // their angle to it)
    open: [2.6, 4.1],
    opening: 0.3,
    // and move apart (the corridor opening up ahead of the camera): their
    // distance to the centre line this many times more
    widen: 1.9,
    // the deck is far enough apart to be drawn by depth from here
    depth: 2.6,
    // the black layer: over the cards as the camera passes them
    dark: [3.35, 4.25],
    modal: [3.95, 4.75],
    grow: [3.95, 5.0],
    content: [4.3, 5.0],
}

export function scene(t: number, vw: number, vh: number): Scene {
    const f = timeline(Math.min(t, T.close[0]), vw, vh, true)
    const D = f.cards[0].persp
    const k = clamp((t - FLY3.move[0]) / (FLY3.move[1] - FLY3.move[0]))
    // (eases in and keeps going: the camera is still moving when the screen is black)
    const travel = k * k * (2 - k)
    const open = ss(FLY3.open[0], FLY3.open[1], t)
    const dark = ss(FLY3.dark[0], FLY3.dark[1], t)
    const apart = 1 + FLY3.widen * open
    const cards: Card3D[] = f.cards.map((c) => ({
        x: c.x * apart,
        y: c.y * apart,
        w: c.w,
        h: c.h,
        r: c.r,
        // (towards edge on on its own side: +90 or -90deg)
        angle: edgeOn(c.rx) + (c.rx - edgeOn(c.rx)) * (1 - FLY3.opening * open),
        scale: c.scale,
        layer: c.layer,
        dark,
        visible: c.visible && dark < 1,
    }))
    const m = timeline(T.modal[1] + 1, vw, vh).modal
    const mo = ss(FLY3.modal[0], FLY3.modal[1], t)
    return {
        axis: f.axis,
        scale: f.scale,
        distance: D,
        camera: D * (1 - FLY3.travel * travel),
        depth: t >= FLY3.depth,
        cards,
        modal: { ...m, visible: mo > 0, opacity: mo, scale: 0.86 + 0.14 * GROW(clamp((t - FLY3.grow[0]) / (FLY3.grow[1] - FLY3.grow[0]))), content: ss(FLY3.content[0], FLY3.content[1], t) },
        intro: f.intro,
    }
}
